<template>
  <div class="matchesTable">
    <b-loading :is-full-page="false" v-model="isLoading" />

    <!-- Match filters -->
    <section
      v-if="matches && matches.length"
      class="is-flex is-justify-content-space-between is-align-items-center"
    >
      <b-field class="my-0" label="Variant" label-position="on-border">
        <b-select v-model="gameDuration">
          <option :value="null" key="variantAll">All</option>
          <option :value="60" key="60">Bullet</option>
          <option :value="300" key="300">Blitz</option>
          <option :value="900" key="900">Rapid</option>
        </b-select>
      </b-field>

      <b-field class="my-0" label="Opponent" label-position="on-border">
        <b-input
          v-model="gameOpponent"
          placeholder="Opponent first name"
        ></b-input>
      </b-field>

      <b-field label="Ending" label-position="on-border">
        <b-select v-model="gameResult">
          <option :value="null">All</option>
          <option value="forfeit">forfeit</option>
          <option value="checkmate">checkmate</option>
          <option value="stalemate">stalemate</option>
          <option value="threefold_repetition">threefold repetition</option>
          <option value="insufficient_material">insufficient material</option>
          <option value="draw">draw</option>
          <option value="timeout">timeout</option>
          <option value="bare king">bare king</option>
          <option value="resign">resign</option>
          <option value="draw by agreement">draw by agreement</option>
          <option value="disqualified">disqualified</option>
        </b-select>
      </b-field>

      <b-field class="my-0" label="Result" label-position="on-border">
        <b-select v-model="gameScore">
          <option :value="null" key="all">All</option>
          <option :value="1" key="WIN">WIN</option>
          <option :value="0.5" key="DRAW">DRAW</option>
          <option :value="0" key="LOSS">LOSS</option>
        </b-select>
      </b-field>

      <b-field class="my-0" label="Per page" label-position="on-border">
        <b-select v-model="gamePageSize">
          <option :value="10" key="10">10</option>
          <option :value="50" key="50">50</option>
          <option :value="100" key="100">100</option>
        </b-select>
      </b-field>

      <b-button
        type="is-info"
        @click="
          gamePage = 1;
          queryMatches();
        "
      >
        Search
      </b-button>
    </section>

    <!-- Matches -->
    <section class="mt-4" v-show="matches && matches.length">
      <MatchesTable
        :matches="matches"
        :onlinePlayers="getOnlinePlayers"
        :challenge="true"
      />
      <b-pagination
        :total="total"
        v-model="gamePage"
        @input="queryMatches()"
        :range-before="2"
        :range-after="2"
        order="is-centered"
        :per-page="gamePageSize"
      ></b-pagination>
    </section>
  </div>
</template>

<script>
import MatchesTable from "./MatchesTable.vue";
import { MatchesAPI } from "@/services/api";
import { mapGetters } from "vuex";
import { aiPlayer } from "vue-chess-shared/utils/ai-helper";

export default {
  components: { MatchesTable },

  name: "MatchesTableFiltered",

  props: ["username"],

  data() {
    return {
      isLoading: false,
      matches: [],
      total: null,
      gamePage: 1,
      gameDuration: null,
      gameOpponent: null,
      gameResult: null,
      gameScore: null,
      gamePageSize: 10,
    };
  },

  computed: {
    ...mapGetters(["getOnlinePlayers"]),

    filters() {
      return {
        gameDuration: this.gameDuration,
        gameOpponent: this.gameOpponent,
        gameResult: this.gameResult,
        gameScore: this.gameScore,
        gamePageSize: this.gamePageSize,
        gamePage: this.gamePage,
      };
    },
  },

  watch: {
    gamePage: {
      handler() {
        this.queryMatches();
      },
    },

    filters: function() {
      if (
        this.$route.query.gamePage !== this.gamePage ||
        this.$route.query.gamePageSize !== this.gamePageSize
      ) {
        const newQuery = { ...this.$route.query, ...this.filters };
        history.replaceState(
          {},
          null,
          "#" +
            this.$route.path +
            "?" +
            Object.keys(newQuery)
              .filter((key) => newQuery[key] != null)
              .map((key) => {
                return `${encodeURIComponent(key)}=${encodeURIComponent(
                  newQuery[key]
                )}`;
              })
              .join("&")
        );
      }
    },
  },

  methods: {
    async queryMatches() {
      this.isLoading = true;
      const response = await MatchesAPI.getPlayerMatches(this.username, {
        duration: this.gameDuration,
        opponent: this.gameOpponent ? this.gameOpponent : null,
        result: this.gameResult,
        score: this.gameScore,
        skip: (this.gamePage - 1) * this.gamePageSize,
        take: this.gamePageSize,
      });
      if (response) {
        this.total = response.total;
        this.matches = response.data.map((match) => {
          match.player_1_score = match.player_1_score / 2;
          match.player_2_score = match.player_2_score / 2;
          if (!match.player1) {
            match.player1 = aiPlayer(match.player_1_id);
            if (!match.variant) match.variant = "vs. AI";
          } else {
            match.player1.stats = match.player1_rating;
          }
          if (!match.player2) {
            match.player2 = aiPlayer(match.player_2_id);
            if (!match.variant) match.variant = "vs. AI";
          } else {
            match.player2.stats = match.player2_rating;
          }
          if (match.scenario_id) match.variant = "Chessie's";
          return match;
        });
      }
      this.isLoading = false;
    },
  },

  created() {
    if (this.$route.query.gameDuration) {
      this.gameDuration = this.$route.query.gameDuration;
    }
    if (this.$route.query.gameOpponent) {
      this.gameOpponent = this.$route.query.gameOpponent;
    }
    if (this.$route.query.gameResult) {
      this.gameResult = this.$route.query.gameResult;
    }
    if (this.$route.query.gameScore) {
      this.gameScore = this.$route.query.gameScore;
    }
    if (this.$route.query.gamePageSize) {
      this.gamePageSize = parseInt(this.$route.query.gamePageSize);
    }
    if (this.$route.query.gamePage) {
      this.gamePage = parseInt(this.$route.query.gamePage);
    }

    this.queryMatches();
  },
};
</script>

<style scoped>
.matchesTable {
  position: relative;
}
</style>
